/**
 * Frontend RBAC helpers — UX only. Backend authorization is the source of truth.
 */

const SYSTEM_ROLE_EXACT = new Set(['offline_access', 'uma_authorization']);
const SYSTEM_ROLE_PREFIXES = ['default-roles-', 'default_roles_'];

export function normalizeRole(role: string): string {
  return role.trim().toLowerCase().replace(/-/g, '_');
}

export function isSystemRole(role: string): boolean {
  const key = normalizeRole(role);
  if (!key) return true;
  if (SYSTEM_ROLE_EXACT.has(key)) return true;
  return SYSTEM_ROLE_PREFIXES.some((prefix) => key.startsWith(prefix.replace(/-/g, '_')));
}

export function isEvalRole(role: string): boolean {
  const key = normalizeRole(role);
  return Boolean(key) && key.startsWith('eval_');
}

export function productRoles(roles: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const role of roles) {
    const key = normalizeRole(role);
    if (!key || isSystemRole(key) || isEvalRole(key)) continue;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(key);
    }
  }
  return out;
}

export function hasRole(roles: string[], role: string): boolean {
  const granted = new Set(productRoles(roles));
  return granted.has(normalizeRole(role));
}

export function hasAnyRole(roles: string[], wanted: string[]): boolean {
  const granted = new Set(productRoles(roles));
  return wanted.some((role) => granted.has(normalizeRole(role)));
}

const PLATFORM_ADMIN_ROLES = ['platform_admin', 'super_admin'];
const TENANT_ADMIN_ROLES = ['tenant_admin', 'org_admin', 'clinic_admin', 'admin'];
const CLINICAL_ROLES = [
  'clinician',
  'specialist',
  'nurse',
  'attending_physician',
  'doctor',
  'care_manager',
  'resident_physician',
  'registered_nurse',
];
const SUPPORT_ROLES = ['support_engineer', 'support_operator', 'support_admin'];
const AUDIT_ROLES = ['auditor', 'security_auditor', 'readonly_admin'];

export function canAccessAdmin(roles: string[]): boolean {
  return hasAnyRole(roles, [
    ...PLATFORM_ADMIN_ROLES,
    ...TENANT_ADMIN_ROLES,
    'rag_admin',
    'readonly_admin',
    'security_auditor',
    ...AUDIT_ROLES,
    ...SUPPORT_ROLES,
  ]);
}

export function canAccessClinicalWorkspace(roles: string[]): boolean {
  return hasAnyRole(roles, [...PLATFORM_ADMIN_ROLES, ...TENANT_ADMIN_ROLES, ...CLINICAL_ROLES]);
}

export function canAccessEvaluation(roles: string[]): boolean {
  return hasAnyRole(roles, [...PLATFORM_ADMIN_ROLES, 'tenant_admin']);
}

export function canAccessSupport(roles: string[]): boolean {
  return hasAnyRole(roles, [...PLATFORM_ADMIN_ROLES, 'tenant_admin', ...SUPPORT_ROLES]);
}

export function canAccessAudit(roles: string[]): boolean {
  return hasAnyRole(roles, [
    ...PLATFORM_ADMIN_ROLES,
    'tenant_admin',
    'org_admin',
    ...AUDIT_ROLES,
    'support_engineer',
  ]);
}

export function isReadOnlyPanelUser(roles: string[]): boolean {
  const product = productRoles(roles);
  if (!product.length) return false;
  const readOnly = hasAnyRole(product, ['auditor', 'security_auditor', 'readonly_user']);
  const canWrite = hasAnyRole(product, [
    ...PLATFORM_ADMIN_ROLES,
    ...TENANT_ADMIN_ROLES,
    ...CLINICAL_ROLES,
  ]);
  return readOnly && !canWrite;
}

/** Admin sidebar visibility by path and role (UX mirror of backend admin matrix). */
export function canAccessAdminNav(path: string, roles: string[]): boolean {
  const normalizedPath = path.replace(/\/$/, '') || '/admin';

  if (hasAnyRole(roles, [...PLATFORM_ADMIN_ROLES, 'tenant_admin', 'org_admin'])) {
    return true;
  }

  if (hasAnyRole(roles, ['auditor', 'security_auditor'])) {
    return [
      '/admin',
      '/admin/system-log',
    ].includes(normalizedPath);
  }

  if (hasAnyRole(roles, SUPPORT_ROLES)) {
    return [
      '/admin',
      '/admin/run-lookup',
      '/admin/system-log',
      '/admin/status',
      '/admin/traffic-inspector',
    ].includes(normalizedPath);
  }

  return normalizedPath === '/admin' && canAccessAdmin(roles);
}
